import { isNil } from 'lodash-es'
import { DateTime } from 'luxon'
import { useCallback } from 'react'

import { isInputConfig } from '../../../../application/formBuilder/config'
import { useProposal } from '../../../../application/product/proposal/ProposalProvider'
import {
  AssetDisclosureChange,
  Data,
  getCoreAsset,
  getCurrentQuote,
  PolicyChangeType,
  ProfileDisclosureChange,
  Proposal,
  ProposalAdjustmentRequest,
  QuoteDisclosureChange,
  RequestCoverOptionChange,
  SelectVoluntaryExcessChange,
} from '../../../../domain'
import { useAdjustments } from '../../../../state'
import {
  AssetChangeConfig,
  ChangeConfig,
  ChangeType,
  FormConfig,
  getFormElements,
  ProfileChangeConfig,
  ProfilesRepeaterConfig,
  QuoteChangeConfig,
  RequestCoverOptionChangeConfig,
  StartDateChangeConfig,
  useConfig,
  VoluntaryExcessChangeConfig,
} from '../../../../state/configuration'
import { hasUpdates, toUpdates } from '../../helpers'

type ProposalAdjustmentReducer<T extends ChangeConfig> = (
  proposalAdjustment: ProposalAdjustmentRequest,
  field: string,
  adjustment: T,
) => ProposalAdjustmentRequest

const toAssetReducer =
  (
    existingData: Data,
    updatedData: Data,
    proposal: Proposal,
  ): ProposalAdjustmentReducer<AssetChangeConfig> =>
  (proposalAdjustment, field, adjustment) => {
    const quote = getCurrentQuote(proposal)
    const { assetCode } = getCoreAsset(quote)

    const updates = toUpdates(existingData, updatedData)

    const changes: AssetDisclosureChange[] = updates(field, adjustment).map((update) => ({
      changeType: PolicyChangeType.ASSET_DISCLOSURE_CHANGE,
      assetCode,
      update,
    }))

    return {
      ...proposalAdjustment,
      changes: [...proposalAdjustment.changes, ...changes],
    }
  }

const toQuoteQuoteReducer =
  (existingData: Data, updatedData: Data): ProposalAdjustmentReducer<QuoteChangeConfig> =>
  (proposalAdjustment, field, adjustment) => {
    const updates = toUpdates(existingData, updatedData)

    const changes: QuoteDisclosureChange[] = updates(field, adjustment).map((update) => ({
      changeType: PolicyChangeType.QUOTE_DISCLOSURE_CHANGE,
      update,
    }))

    return {
      ...proposalAdjustment,
      changes: [...proposalAdjustment.changes, ...changes],
    }
  }

const toProfileReducer =
  (
    existingData: Data,
    updatedData: Data,
    profileCode?: string,
  ): ProposalAdjustmentReducer<ProfileChangeConfig> =>
  (proposalAdjustment, field, adjustment) => {
    const updates = toUpdates(existingData, updatedData)

    const changes: ProfileDisclosureChange[] = updates(field, adjustment).map(
      (update) => ({
        changeType: PolicyChangeType.PROFILE_DISCLOSURE_CHANGE,
        profileCode,
        update,
      }),
    )

    return {
      ...proposalAdjustment,
      changes: [...proposalAdjustment.changes, ...changes],
    }
  }

const useProfilesRepeater = () => {
  const formElements = useConfig(getFormElements)
  const { getAdjustment } = useAdjustments()

  return useCallback(
    (
        existingData: Data,
        updatedData: Data,
        proposal: Proposal,
      ): ProposalAdjustmentReducer<ProfilesRepeaterConfig> =>
      (proposalAdjustment, field, adjustment) => {
        const quote = getCurrentQuote(proposal)

        const request = quote.insuredProfiles.reduce((acc, { profileCode }, index) => {
          const existingProfile = existingData[field]?.[index] ?? {}
          const updatedProfile = updatedData[field]?.[index] ?? {}

          return (
            adjustment.adjustments.reduce<ProposalAdjustmentRequest>((a, f) => {
              const profileReducer = toProfileReducer(
                existingProfile,
                updatedProfile,
                profileCode,
              )

              const adjustment = getAdjustment(f)

              switch (adjustment.changeType) {
                case ChangeType.PROFILE_DISCLOSURE:
                  return {
                    ...acc,
                    ...profileReducer(a, f, adjustment),
                  }
                default:
                  return acc
              }
            }, acc) ?? acc
          )
        }, proposalAdjustment)

        return {
          ...proposalAdjustment,
          changes: [...proposalAdjustment.changes, ...request.changes],
        }
      },
    [formElements, getAdjustment],
  )
}

const toVoluntaryExcessReducer =
  (updatedData: Data): ProposalAdjustmentReducer<VoluntaryExcessChangeConfig> =>
  (proposalAdjustment, field) => {
    const change: SelectVoluntaryExcessChange = {
      changeType: PolicyChangeType.SELECT_VOLUNTARY_EXCESS,
      excess: updatedData[field],
    }

    return {
      ...proposalAdjustment,
      changes: [...proposalAdjustment.changes, change],
    }
  }

const toStartDateDisclosureReducer =
  (updatedData: Data): ProposalAdjustmentReducer<StartDateChangeConfig> =>
  (proposalAdjustment, field) => ({
    ...proposalAdjustment,
    coverStart: !isNil(updatedData[field])
      ? DateTime.fromISO(updatedData[field]).toISO()
      : proposalAdjustment.coverStart,
  })

const toCoverOptionReducer =
  (updatedData: Data): ProposalAdjustmentReducer<RequestCoverOptionChangeConfig> =>
  (proposalAdjustment, field, adjustment) => {
    const change: RequestCoverOptionChange = {
      changeType: PolicyChangeType.REQUEST_COVER_OPTION,
      coverOption: adjustment.coverCode,
      selected: updatedData[field],
    }

    return {
      ...proposalAdjustment,
      changes: [...proposalAdjustment.changes, change],
    }
  }

export const useProposalAdjustmentRequest = (form: FormConfig, applyRating: boolean) => {
  const { proposal } = useProposal()
  const { getAdjustment } = useAdjustments()

  const toProfilesRepeater = useProfilesRepeater()

  return useCallback(
    (existingData: Data, updatedData: Data): ProposalAdjustmentRequest => {
      const profileReducer = toProfileReducer(existingData, updatedData)
      const voluntaryExcessReducer = toVoluntaryExcessReducer(updatedData)
      const assetReducer = toAssetReducer(existingData, updatedData, proposal)
      const quoteReducer = toQuoteQuoteReducer(existingData, updatedData)
      const coverOptionReducer = toCoverOptionReducer(updatedData)
      const startDateReducer = toStartDateDisclosureReducer(updatedData)

      const profilesRepeater = toProfilesRepeater(existingData, updatedData, proposal)

      return form
        .filter(isInputConfig)
        .filter(hasUpdates(existingData, updatedData))
        .reduce(
          (proposalAdjustment: ProposalAdjustmentRequest, input) => {
            const adjustment = getAdjustment(input.name)

            switch (adjustment.changeType) {
              case ChangeType.PROFILE_DISCLOSURE:
                return profileReducer(proposalAdjustment, input.name, adjustment)
              case ChangeType.ASSET_DISCLOSURE:
                return assetReducer(proposalAdjustment, input.name, adjustment)
              case ChangeType.QUOTE_DISCLOSURE:
                return quoteReducer(proposalAdjustment, input.name, adjustment)
              case ChangeType.START_DATE_CHANGE:
                return startDateReducer(proposalAdjustment, input.name, adjustment)
              case ChangeType.REQUEST_COVER_OPTION:
                return coverOptionReducer(proposalAdjustment, input.name, adjustment)
              case ChangeType.VOLUNTARY_EXCESS:
                return voluntaryExcessReducer(proposalAdjustment, input.name, adjustment)

              case ChangeType.PROFILES_REPEATER:
                return profilesRepeater(proposalAdjustment, input.name, adjustment)

              default:
                throw new Error('Invalid adjustment type')
            }
          },
          {
            applyRating,
            changes: [],
          },
        )
    },
    [form, applyRating, proposal, getAdjustment, toProfilesRepeater],
  )
}
